import { useState } from "react";
import { Upload, Trash2, FileText, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { PageHeader } from "@/components/Common";
import { api, formatErr, fmtDate, useFetch } from "@/lib/api";
import { fmtSize, downloadResource } from "@/pages/app/Resources";

const BLANK = { title: "", description: "", category: "Templates" };

export default function AdminResources() {
  const { data, setData } = useFetch("/resources");
  const [f, setF] = useState(BLANK);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose a file to upload");
    const fd = new FormData();
    Object.entries(f).forEach(([k, v]) => fd.append(k, v));
    fd.append("file", file);
    setBusy(true);
    try {
      const { data: r } = await api.post("/admin/resources", fd);
      setData((p) => [r, ...(p || [])]);
      setF(BLANK);
      setFile(null);
      e.target.reset();
      toast.success("File published to members");
    } catch (err) { toast.error(formatErr(err)); }
    setBusy(false);
  };

  const remove = async (r) => {
    try {
      await api.delete(`/admin/resources/${r.id}`);
      setData((p) => p.filter((x) => x.id !== r.id));
      toast.success("File removed");
    } catch (err) { toast.error(formatErr(err)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Member library" title="Business files" desc="Upload templates, SOPs and documents included with membership." />
      <div className="grid gap-8 lg:grid-cols-[360px_1fr]">
        <form onSubmit={upload} data-testid="resource-upload-form" className="h-fit space-y-4 rounded-md border border-stone-200 bg-[#F5F5F0] p-6">
          <div className="space-y-1.5"><Label>Title</Label><Input data-testid="resource-title-input" required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="bg-white" /></div>
          <div className="space-y-1.5"><Label>Category</Label><Input data-testid="resource-category-input" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="bg-white" /></div>
          <div className="space-y-1.5"><Label>Description</Label><Textarea data-testid="resource-description-input" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="bg-white" /></div>
          <div className="space-y-1.5"><Label>File (max 25MB)</Label><Input data-testid="resource-file-input" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="bg-white" /></div>
          <Button type="submit" disabled={busy} data-testid="resource-upload-button" className="w-full"><Upload className="mr-2 h-4 w-4" />{busy ? "Uploading…" : "Upload file"}</Button>
        </form>
        <div className="space-y-3">
          {(data || []).map((r) => (
            <div key={r.id} data-testid={`admin-resource-${r.id}`} className="flex items-center gap-4 rounded-md border border-stone-200 bg-white p-5">
              <FileText className="h-5 w-5 shrink-0 text-stone-500" strokeWidth={1.5} />
              <div className="min-w-0 flex-1">
                <p className="font-medium text-stone-900">{r.title}</p>
                <p className="text-xs text-stone-500">{r.category} · {r.filename} · {fmtSize(r.size)} · {fmtDate(r.created_at)}</p>
              </div>
              <Button size="icon" variant="ghost" data-testid={`admin-resource-download-${r.id}`} onClick={() => downloadResource(r).catch((e) => toast.error(formatErr(e)))}><Download className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" data-testid={`admin-resource-delete-${r.id}`} onClick={() => remove(r)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          {data?.length === 0 && <p className="text-sm text-stone-500">No files uploaded yet.</p>}
        </div>
      </div>
    </div>
  );
}
